import type { BiomeId, StationKind } from '../../net/protocol';
import type { DecalSpec } from './decals';
import type { PropId } from '../models/props';

/**
 * A biome's whole visual identity: floor and wall colour, the light that falls
 * on it, and how thick the air is.
 *
 * Purely aesthetic, by design. Nothing here touches a stat — difficulty comes
 * from the zone's `enemyLevel`, which this file never reads. See
 * `doc/grind/architecture/ZONE-GENERATION.md`.
 *
 * Colours are linear RGB triples for Babylon, not CSS. They are deliberately
 * darker than they look: the pixel post-process quantises on top of them.
 */
import { type Cell, GROUND } from '../models/atlas';

/**
 * World units one ground-sheet floor texture covers. The 512² ground cells (2026-09-29) are four
 * times the scene cells' resolution and painted at a coarser scale — about twenty cobbles across —
 * so they cover more ground: at 6 a cobble is ~0.3 units, a third of a character's width. Chosen.
 */
export const GROUND_FLOOR_UNITS = 6;
/** The same for a wall face; a wall is 2.6 tall, so 4 shows about two courses of blocks. Chosen. */
export const GROUND_WALL_UNITS = 4;
import { shadeColour, type Pattern } from './textures';

/**
 * How a biome's rooms end (2026-09-29). Reported: *could walls be more artistically interesting?
 * Like in a forest/jungle we have trees, in a dungeon solid brick walls, maybe water or lava, not
 * just plain walls.* Every biome used to end in the same 2.6-high wall over black. **What is solid
 * does not change** — the tile boundary is the edge a body stops at in every biome — only what is
 * drawn there. All chosen by looking.
 */
export interface BiomeEdge {
  /** The wall's height. Low (a rim, a bank) where what lies beyond is the point. */
  height: number;
  /**
   * What fills the void beyond the walls, below the floor: the biome's `liquid`, lit by its own
   * `liquidGlow` — lava past a caldera's rim, black water past a marsh's bank. `ground` is the
   * biome's own floor, darker and a step down: the forest floor the thicket's trees stand on. Absent
   * is the black it always was.
   */
  beyond?: 'liquid' | 'ground';
  /** Trees standing beyond the walls, the far ones tall and the near ones low. */
  trees?: boolean;
  /**
   * How bright the wall's emissive cap is, as a fraction of `trim`. Absent is 1. Chosen: the
   * thicket's 0.25 — at full strength a knee-high hedge capped in gold read as a garden bed.
   */
  trim?: number;
  /** Masonry pillars along the walls, one per tile edge. */
  pillars?: boolean;
}

/** A biome's light sources. All of it chosen by looking. */
export interface BiomeLights {
  /** The prop that holds the flame. */
  prop: PropId;
  /** On the wall's face, 1.0 up (a torch), or standing on top of a low rim (a brazier, a lantern). */
  mount: 'wall' | 'rim';
  /** Its fitted size; absent is the prop's own. */
  size?: number;
  /** The share of eligible far-wall faces that get one. */
  share: number;
  /** The flame's and the light's colour. */
  colour: readonly [number, number, number];
  /** Where the flame sits: this far below the prop's top, and this far out along its facing. */
  flameDrop: number;
  flameOut: number;
}

/** The wall every biome had before `BiomeEdge`. */
export const PLAIN_EDGE: BiomeEdge = { height: 2.6 };

export interface BiomeStyle {
  /**
   * What lies painted on its floor, a quarter of the tiles or so (`zone/decals.ts`). Flat, never
   * solid-looking: the rubble that looked solid was switched off. Absent is a bare floor — the hub.
   */
  decals?: readonly DecalSpec[];
  /**
   * What lights its rooms, along the far walls (`ZoneRenderer.buildLights`, `TorchLights`). Absent
   * is no light sources: the hub's lamps are its own.
   */
  lights?: BiomeLights;
  /**
   * How its `POOL` tiles are drawn: liquid (absent) or a **pit** where the floor has fallen in — the
   * crypts', since 2026-10-01. Either way the low obstacle of `design/LEVEL-FIXTURES.md`: a body walks
   * round it, a shot crosses it.
   */
  pool?: 'liquid' | 'pit';
  /** How rooms end. Absent is `PLAIN_EDGE`. */
  edge?: BiomeEdge;
  id: BiomeId;
  /** Human-readable, for the HUD. */
  name: string;
  floor: [number, number, number];
  /** Slight variation so a large floor is not one flat plane. */
  floorAlt: [number, number, number];
  wall: [number, number, number];
  /** Emissive trim along wall tops — the biome's signature light. */
  trim: [number, number, number];
  fog: [number, number, number];
  fogDensity: number;
  ambient: [number, number, number];
  ambientIntensity: number;
  sun: [number, number, number];
  sunIntensity: number;
  /** Backdrop beyond the zone edge. */
  clear: [number, number, number];
  /**
   * How the floor and walls are patterned.
   *
   * Painted procedurally at zone build — see `game/zone/textures.ts`. A biome was
   * a palette and nothing else before this, so every environment was the same
   * geometry in a different colour; the pattern is what makes a crypt read as cut
   * stone and the wastes as wind-blown sediment.
   */
  floorPattern: Pattern;
  wallPattern: Pattern;
  /**
   * A painted surface from the scene atlas, replacing the procedural pattern above.
   *
   * **Optional, and four of the seven biomes deliberately have none.** The scene sheet's sixteen
   * cells are stone, timber, metal and ground — a vocabulary of *real materials* — and three
   * biomes are not made of any of them. The Whispering Abyss has no structure to read at all
   * (its pattern is `noise`, and that is the design); the Ashen Caldera wants volcanic rock and
   * the sheet's nearest offer is dry cracked earth, which the Wastes has a better claim on.
   *
   * Left absent rather than approximated. A biome with no cell keeps the procedural pattern it
   * has always had, which is a complete and tested surface — so this is an upgrade where the art
   * fits and a no-op where it does not, rather than a migration that has to cover everything.
   *
   * The **pattern stays declared either way**, and that is not redundancy: it is the fallback for
   * `?textures=0` and safe mode, exactly as `ArmourProfile.material` is for a character cell.
   */
  /**
   * How bright the painted floor and wall read, as a multiplier on the texture (1 = as painted).
   * The ground sheet (2026-09-29) is brighter than the scene cells it replaced, and some of it far
   * brighter — a lava crack, a sandstone wall — so each biome takes its own down to sit under the
   * monsters rather than compete with them. Chosen by looking. Absent is 1.
   */
  floorTint?: number;
  wallTint?: number;
  floorCell?: Cell;
  wallCell?: Cell;
  /**
   * How many world units one repeat of the cell should span.
   *
   * **Not a repeat count**, because a count would be measured against `tileSize` (eight units) and
   * would silently change meaning if that ever moved. This is the size of the *thing depicted*: the
   * cobbles cell holds about six stones across, so at 2 units a stone is roughly a third of a unit
   * — a cobble rather than a paving slab the size of the player, which is what one repeat per tile
   * produced (owner's report: *the stones are too big and badly repeatable*).
   *
   * Absent means one repeat per tile, the original behaviour.
   */
  surfaceUnits?: number;
  /**
   * The same, for walls and structures, when they want a coarser scale than the ground.
   *
   * **Floors and walls are not the same size of surface and sharing one number showed it.** The hub
   * wants cobbles at 2 units so a stone is a quarter of the character's width; its houses are clad
   * in iron-bound timber, and at 2 units that put four iron straps across every wall and four more
   * across every structure's top face — a plaid grid rather than timber framing. A strap belongs
   * once per storey.
   *
   * Falls back to `surfaceUnits`, so a biome only states this where the two genuinely differ.
   */
  wallUnits?: number;
  /**
   * The surface of a `POOL` tile: bog water, lava, void mist.
   *
   * A biome's liquid rather than "water", because the tile kind is impassable
   * liquid and nothing more — there is no hazard system, so lava does not burn.
   * Every biome declares one even where no pool is ever placed (the hub is
   * hand-drawn and the crypts' terrain is fallen masonry), because a palette with a
   * hole in it is a black quad the first time a profile changes.
   */
  liquid: [number, number, number];
  /** Emissive lift on the liquid — how much of its own light it gives off. */
  liquidGlow: number;
}

const STYLES: Record<BiomeId, BiomeStyle> = {
  // Town. Warmer and brighter than anywhere else on purpose: arriving here
  // should feel like stepping out of the dark, and it is the one place with no
  // reason to hide anything from the player.
  BIOME_HUB: {
    id: 'BIOME_HUB',
    floorCell: GROUND.hubFloor,
    wallCell: GROUND.hubWall,
    surfaceUnits: GROUND_FLOOR_UNITS,
    wallUnits: GROUND_WALL_UNITS,
    floorTint: 0.75,
    wallTint: 0.8,
    name: 'Sanctuary',
    floor: [0.26, 0.23, 0.2],
    floorAlt: [0.21, 0.185, 0.16],
    wall: [0.16, 0.14, 0.12],
    trim: [0.85, 0.62, 0.26],
    fog: [0.05, 0.042, 0.035],
    fogDensity: 0.012,
    ambient: [0.2, 0.17, 0.14],
    ambientIntensity: 0.55,
    sun: [1, 0.9, 0.72],
    sunIntensity: 1.1,
    clear: [0.03, 0.025, 0.022],
    floorPattern: { kind: 'cobbles', cell: 26, mortar: 4, jitter: 0.3, grain: 0.18 },
    wallPattern: { kind: 'masonry', cell: 30, mortar: 4, jitter: 0.22, grain: 0.2 },
    // The sheet's cobbles cell is a town street by description and by look, and the
    // iron-bound timber is what the houses are built of. The clearest two fits on the sheet.
    // Six stones per two units puts a cobble at a third of a unit — a quarter of the character's
    // width, which is what a street reads as.
    // One iron strap per two-and-a-half units of wall, so the banding reads as framing on a
    // house rather than as a pattern on a slab.
    // Never drawn: the hub is hand-drawn and places no pools. A fountain's water,
    // for the day it does.
    liquid: [0.12, 0.22, 0.3],
    liquidGlow: 0.1,
  },

  // Narrow stone corridors, dim torchlight, green fog.
  BIOME_CRYPTS: {
    id: 'BIOME_CRYPTS',
    pool: 'pit',
    lights: { prop: 'crypt_wall_torch', mount: 'wall', share: 0.25, colour: [1.0, 0.55, 0.2], flameDrop: 0.14, flameOut: 0.28 },
    decals: [{ id: 'crypts_bones', size: 2.4 }, { id: 'crypts_stain', size: 2.0 }],
    edge: { height: 2.6, pillars: true },
    floorCell: GROUND.cryptsFloor,
    wallCell: GROUND.cryptsWall,
    surfaceUnits: GROUND_FLOOR_UNITS,
    wallUnits: GROUND_WALL_UNITS,
    floorTint: 0.85,
    wallTint: 0.8,
    name: 'Forgotten Crypts',
    floor: [0.17, 0.175, 0.155],
    floorAlt: [0.135, 0.142, 0.126],
    wall: [0.1, 0.105, 0.092],
    trim: [0.16, 0.3, 0.14],
    fog: [0.022, 0.04, 0.026],
    fogDensity: 0.027,
    ambient: [0.06, 0.08, 0.06],
    ambientIntensity: 0.3,
    sun: [0.95, 0.86, 0.66],
    sunIntensity: 0.85,
    clear: [0.012, 0.02, 0.014],
    floorPattern: { kind: 'masonry', cell: 34, mortar: 5, jitter: 0.28, grain: 0.28 },
    wallPattern: { kind: 'masonry', cell: 28, mortar: 5, jitter: 0.3, grain: 0.32 },
    // Charcoal brick underfoot, pale granite blocks above — the value contrast between the two
    // is what stops a corridor reading as one continuous surface bent at the wall line.
    // Cut stone is bigger than a cobble and a crypt's blocks should read as masonry a person could
    // not lift, so this is the loosest scale of the four.
    // Standing groundwater in a flooded crypt. The crypts' own terrain profile is
    // all fallen masonry, so this is the seam a league or an affix would use.
    liquid: [0.07, 0.12, 0.11],
    liquidGlow: 0.08,
  },

  // Open volcanic plateaus, lava glow, embers.
  BIOME_CALDERA: {
    id: 'BIOME_CALDERA',
    lights: { prop: 'caldera_charred_brazier', mount: 'rim', size: 1.1, share: 0.25, colour: [1.0, 0.45, 0.15], flameDrop: 0.1, flameOut: 0 },
    decals: [{ id: 'caldera_ash', size: 2.6, glow: 0.5 }, { id: 'caldera_vent', size: 1.8, glow: 0.9 }],
    edge: { height: 0.55, beyond: 'liquid' },
    floorCell: GROUND.calderaFloor,
    wallCell: GROUND.calderaWall,
    surfaceUnits: GROUND_FLOOR_UNITS,
    wallUnits: GROUND_WALL_UNITS,
    floorTint: 0.6,
    wallTint: 0.9,
    name: 'Ashen Caldera',
    floor: [0.249, 0.155, 0.124],
    floorAlt: [0.165, 0.1, 0.075],
    wall: [0.095, 0.052, 0.043],
    trim: [0.95, 0.32, 0.06],
    fog: [0.07, 0.028, 0.016],
    fogDensity: 0.026,
    ambient: [0.12, 0.06, 0.04],
    ambientIntensity: 0.42,
    sun: [1, 0.62, 0.34],
    sunIntensity: 1.18,
    clear: [0.05, 0.018, 0.01],
    floorPattern: { kind: 'shards', cell: 30, mortar: 6, jitter: 0.42, grain: 0.3 },
    wallPattern: { kind: 'shards', cell: 24, mortar: 7, jitter: 0.45, grain: 0.34 },
    // Lava, and the brightest liquid in the game: in a biome lit at 0.34 ambient it
    // is the pool that lights the room rather than the room lighting the pool.
    liquid: [0.85, 0.24, 0.05],
    liquidGlow: 0.75,
  },

  // Branching subterranean paths, dark purple fog.
  BIOME_ABYSS: {
    id: 'BIOME_ABYSS',
    lights: { prop: 'abyss_floating_rune_monolith', mount: 'rim', size: 1.7, share: 0.25, colour: [0.65, 0.4, 1.0], flameDrop: 0.5, flameOut: 0.25 },
    decals: [{ id: 'abyss_crack', size: 2.0, glow: 0.8 }, { id: 'abyss_rune', size: 2.6, glow: 0.6 }],
    edge: { height: 0.5, beyond: 'liquid' },
    floorCell: GROUND.abyssFloor,
    wallCell: GROUND.abyssWall,
    surfaceUnits: GROUND_FLOOR_UNITS,
    wallUnits: GROUND_WALL_UNITS,
    floorTint: 0.85,
    wallTint: 0.9,
    name: 'The Whispering Abyss',
    floor: [0.226, 0.203, 0.312],
    floorAlt: [0.115, 0.105, 0.165],
    wall: [0.082, 0.07, 0.12],
    trim: [0.42, 0.24, 0.85],
    fog: [0.035, 0.022, 0.06],
    // Void mist is thicker than anywhere else — the spec's vision penalty,
    // expressed visually rather than as a stat.
    fogDensity: 0.03,
    ambient: [0.07, 0.06, 0.12],
    ambientIntensity: 0.44,
    sun: [0.62, 0.54, 0.95],
    sunIntensity: 1.09,
    clear: [0.018, 0.012, 0.032],
    floorPattern: { kind: 'noise', cell: 18, mortar: 0, jitter: 0.5, grain: 0.4 },
    wallPattern: { kind: 'noise', cell: 14, mortar: 0, jitter: 0.55, grain: 0.45 },
    // Void mist pooled in the low ground.
    liquid: [0.2, 0.1, 0.36],
    liquidGlow: 0.4,
  },

  // Unbounded open wasteland, dust storms, harsh sun.
  BIOME_WASTES: {
    id: 'BIOME_WASTES',
    lights: { prop: 'crypt_wall_torch', mount: 'wall', share: 0.25, colour: [1.0, 0.65, 0.35], flameDrop: 0.14, flameOut: 0.28 },
    decals: [{ id: 'wastes_skull', size: 1.4 }, { id: 'wastes_skull_bones', size: 1.7 }],
    floorCell: GROUND.wastesFloor,
    wallCell: GROUND.wastesWall,
    surfaceUnits: GROUND_FLOOR_UNITS,
    wallUnits: GROUND_WALL_UNITS,
    floorTint: 0.6,
    wallTint: 0.45,
    name: 'Apocalyptic Wastes',
    floor: [0.24, 0.21, 0.165],
    floorAlt: [0.2, 0.176, 0.135],
    wall: [0.115, 0.1, 0.08],
    trim: [0.7, 0.6, 0.36],
    fog: [0.075, 0.066, 0.05],
    fogDensity: 0.022,
    ambient: [0.13, 0.12, 0.1],
    ambientIntensity: 0.42,
    sun: [1, 0.94, 0.78],
    sunIntensity: 1.05,
    clear: [0.045, 0.04, 0.032],
    floorPattern: { kind: 'strata', cell: 22, mortar: 0, jitter: 0.26, grain: 0.24 },
    wallPattern: { kind: 'strata', cell: 18, mortar: 0, jitter: 0.3, grain: 0.28 },
    // Cracked dry earth: the closest thing to a one-word match on the whole sheet. The wall
    // keeps its `strata` pattern — nothing here is wind-blown sediment, and inventing a
    // sandstone out of granite would put the crypts' wall in the open desert.
    // A brine pan: bright, flat and dead. Its glow is low because the wastes have
    // the brightest floor in the game and the pan is lit rather than lighting.
    liquid: [0.3, 0.31, 0.26],
    liquidGlow: 0.2,
  },

  // Drowned ground: sodden, green-black, and the wettest place in the game. The
  // one biome whose *pools* are its identity, so the mottled floor is pitched
  // close to the water and the trim is the only cool light anywhere.
  BIOME_MARSH: {
    id: 'BIOME_MARSH',
    lights: { prop: 'marsh_hanging_lantern', mount: 'rim', size: 1.8, share: 0.25, colour: [1.0, 0.8, 0.45], flameDrop: 0.55, flameOut: 0.2 },
    decals: [{ id: 'marsh_puddle', size: 2.8 }, { id: 'marsh_moss', size: 2.4 }],
    edge: { height: 0.45, beyond: 'liquid' },
    floorCell: GROUND.marshFloor,
    wallCell: GROUND.marshWall,
    surfaceUnits: GROUND_FLOOR_UNITS,
    wallUnits: GROUND_WALL_UNITS,
    floorTint: 0.8,
    wallTint: 0.8,
    name: 'Blighted Marsh',
    floor: [0.158, 0.187, 0.134],
    floorAlt: [0.105, 0.13, 0.09],
    wall: [0.078, 0.09, 0.065],
    trim: [0.34, 0.62, 0.4],
    fog: [0.03, 0.045, 0.032],
    // Second only to the abyss: a marsh is humid, and the fog is the biome's whole
    // atmosphere. It must stay strictly below the abyss's — a test asserts the
    // abyss is the foggiest, because that is the spec's vision penalty made visual.
    fogDensity: 0.028,
    ambient: [0.08, 0.1, 0.08],
    ambientIntensity: 0.4,
    sun: [0.78, 0.86, 0.7],
    sunIntensity: 0.93,
    clear: [0.016, 0.024, 0.017],
    floorPattern: { kind: 'mottled', cell: 26, mortar: 3, jitter: 0.34, grain: 0.34 },
    wallPattern: { kind: 'mottled', cell: 18, mortar: 3, jitter: 0.4, grain: 0.4 },
    // The two green cells, and this is the biome they were painted for. Floor and wall agreeing
    // in hue is wrong everywhere else and right here: a marsh is a place where the ground has
    // grown up the walls.
    // Moss and leaf litter have no natural size, so this is set by what stops it reading as a
    // repeating motif: tighter than the stone biomes.
    // Bog water: dark, still, faintly luminous with rot.
    //
    // **0.5, raised from 0.22 after looking at it.** A pool is defined by its colour
    // and a 0.35-unit drop, and nothing else — no wall goes round it — so at 0.22
    // against this floor it rendered as a smooth dark quad that read as a texture
    // seam rather than as water. The caverns' 0.35 was the calibration point: what a
    // liquid needs is enough of its own light to separate it from the ground it sits
    // in, and the marsh's ground is nearly the same colour as its water.
    liquid: [0.1, 0.19, 0.14],
    liquidGlow: 0.5,
  },

  // Dead timber: the most cluttered biome, so its ground is the plainest. Warm
  // brown against the marsh's green, or the two read as one place.
  BIOME_THICKET: {
    id: 'BIOME_THICKET',
    lights: { prop: 'marsh_hanging_lantern', mount: 'rim', size: 1.8, share: 0.25, colour: [1.0, 0.75, 0.4], flameDrop: 0.55, flameOut: 0.2 },
    decals: [{ id: 'thicket_leaves', size: 2.4 }, { id: 'thicket_roots', size: 3.0 }],
    edge: { height: 1.3, trees: true, beyond: 'ground', trim: 0.25 },
    floorCell: GROUND.thicketFloor,
    wallCell: GROUND.thicketWall,
    surfaceUnits: GROUND_FLOOR_UNITS,
    wallUnits: GROUND_WALL_UNITS,
    floorTint: 0.9,
    wallTint: 0.9,
    name: 'Deadwood Thicket',
    floor: [0.19, 0.155, 0.11],
    floorAlt: [0.155, 0.125, 0.086],
    wall: [0.1, 0.078, 0.055],
    trim: [0.58, 0.44, 0.2],
    fog: [0.04, 0.033, 0.022],
    fogDensity: 0.028,
    ambient: [0.11, 0.1, 0.08],
    ambientIntensity: 0.38,
    sun: [0.95, 0.88, 0.7],
    sunIntensity: 0.9,
    clear: [0.024, 0.02, 0.014],
    floorPattern: { kind: 'mottled', cell: 22, mortar: 3, jitter: 0.28, grain: 0.3 },
    // Tangled branches read as seams, which is the same maths a crystal vein needs.
    wallPattern: { kind: 'veins', cell: 20, mortar: 3, jitter: 0.3, grain: 0.32 },
    // A stagnant forest pool. Same reasoning as the marsh's, one step dimmer
    // because the thicket's floor is warm brown and its water is not.
    liquid: [0.1, 0.14, 0.11],
    liquidGlow: 0.3,
  },

  // Under the mountain: cold blue rock, mineral seams, and the darkest ambient
  // light in the game — the caverns are lit by what is *in* them.
  BIOME_CAVERNS: {
    id: 'BIOME_CAVERNS',
    lights: { prop: 'crypt_wall_torch', mount: 'wall', share: 0.25, colour: [1.0, 0.6, 0.3], flameDrop: 0.14, flameOut: 0.28 },
    decals: [{ id: 'caverns_crystals', size: 1.8, glow: 0.3 }, { id: 'caverns_puddle', size: 2.6 }],
    edge: { height: 2.6, pillars: true },
    floorCell: GROUND.cavernsFloor,
    wallCell: GROUND.cavernsWall,
    surfaceUnits: GROUND_FLOOR_UNITS,
    wallUnits: GROUND_WALL_UNITS,
    floorTint: 0.8,
    wallTint: 0.85,
    name: 'Sunless Caverns',
    floor: [0.169, 0.188, 0.227],
    floorAlt: [0.105, 0.115, 0.142],
    wall: [0.072, 0.082, 0.1],
    trim: [0.3, 0.6, 0.78],
    fog: [0.02, 0.028, 0.038],
    fogDensity: 0.028,
    ambient: [0.055, 0.07, 0.09],
    ambientIntensity: 0.34,
    sun: [0.7, 0.82, 0.95],
    sunIntensity: 0.93,
    clear: [0.01, 0.014, 0.02],
    floorPattern: { kind: 'shards', cell: 26, mortar: 5, jitter: 0.3, grain: 0.28 },
    wallPattern: { kind: 'veins', cell: 16, mortar: 4, jitter: 0.35, grain: 0.3 },
    // Damp dirt underfoot and dead oak above — the thicket is the one biome whose walls are
    // *timber*, which is why it gets the beam cell and the crypts get granite.
    // Mineral water, lit from below by the same seams as the walls.
    liquid: [0.12, 0.3, 0.38],
    liquidGlow: 0.35,
  },
};

/**
 * The town's own palette: what its scenery is made of, over and above the biome.
 *
 * It lives here because **this file is where every colour in the world is declared**,
 * the same rule the HUD follows with `src/styles/tokens.css` — a component that names
 * a colour is the fault, wherever it is. The town needs more than a `BiomeStyle`
 * carries: a shopfront has timber, cloth, skin and five shop colours in it, and none
 * of those belongs in a struct every biome has to fill in.
 *
 * Two of the six are **derived** rather than declared, and that is the point of it
 * being a function: `stone` is the biome's own wall lifted slightly, so battlements
 * and chimneys are the wall's material rather than an imported one, and `glow` is the
 * biome's `trim` — the signature light every wall top already carries — so a lit
 * window, a lantern and a wall cap are one warm colour in one town.
 */
export interface TownPalette {
  /** Battlements and chimneys: the curtain wall's own rock. */
  stone: [number, number, number];
  /** Doors, posts, beams, crates. Stained oak, warmer than any stone here. */
  timber: [number, number, number];
  /**
   * What a townsperson wears under their apron, and their hands and face.
   *
   * Both are pitched **brighter than a lit-room palette would need**, the same
   * decision `models/palette.ts` records for the player and the monsters: the world
   * is near-black and the post-process quantises on top of that, so a realistic value
   * disappears into the wall behind it. A shopkeeper at a realistic 0.22 was
   * genuinely invisible in the first capture of this — the sun comes from the far
   * side, so the only face the camera sees is lit by a 0.55 ambient and nothing else.
   */
  cloth: [number, number, number];
  skin: [number, number, number];
  /** Lit windows, door lamps, street lamps — the biome's trim. */
  glow: [number, number, number];
  /** One colour per workshop, so a shop is identifiable from across the square. */
  accents: Record<StationKind, [number, number, number]>;
}

/**
 * The six frontage colours.
 *
 * Chosen to survive the pixelate post-process's fourteen levels per channel and to stay
 * apart from each other **and** from the town's own warm amber — a shop whose awning is
 * the same colour as the lamp above it is not identifiable, which is the one job this
 * table has. Forge runs hotter and Woodworks cooler than the street light; Atelier and the
 * Vault are the two cold ones and sit a long way apart in hue.
 *
 * The **stash** is the odd one out and looks it: a desaturated stone, because it is not a
 * craft and should not read as a sixth discipline. It is the only fixture here that makes
 * nothing.
 */
const STATION_ACCENTS: Record<StationKind, [number, number, number]> = {
  FORGE: [0.86, 0.34, 0.1],
  TANNERY: [0.6, 0.36, 0.18],
  ATELIER: [0.44, 0.32, 0.8],
  WOODWORKS: [0.32, 0.6, 0.3],
  ARCANE_VAULT: [0.24, 0.68, 0.78],
  STASH: [0.52, 0.5, 0.46],
};

/**
 * One workshop's colour, for anything outside the renderer that needs it.
 *
 * The HUD's icon row uses it, which is the one place a component is allowed to name a
 * colour that is not in `tokens.css`: the whole point is that the swatch under the
 * Forge's icon **is** the colour of the Forge's banner across the square, so the two
 * cannot be allowed to drift into separate declarations. It stays a single source —
 * this file — rather than becoming a token that happens to match.
 */
export function stationAccent(kind: StationKind): [number, number, number] {
  return STATION_ACCENTS[kind];
}

export function townPalette(style: BiomeStyle): TownPalette {
  return {
    stone: shadeColour(style.wall, 0.1),
    timber: [0.33, 0.22, 0.13],
    cloth: [0.34, 0.3, 0.4],
    skin: [0.78, 0.58, 0.43],
    glow: style.trim,
    accents: STATION_ACCENTS,
  };
}

/** The biome to use before a ZONE frame arrives, and for an unknown id. */
export const DEFAULT_BIOME: BiomeId = 'BIOME_CRYPTS';

/**
 * Looks up a biome's style.
 *
 * Falls back rather than throwing: a server that grows a fifth biome should make
 * an older client look wrong, not make it crash.
 */
export function biomeStyle(id: string): BiomeStyle {
  return STYLES[id as BiomeId] ?? STYLES[DEFAULT_BIOME];
}

/** Every style, for tests and tooling. */
export const ALL_BIOMES: readonly BiomeStyle[] = Object.values(STYLES);
